import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  ChevronDown,
  ChevronRight,
  Mail,
  Users,
  Activity,
  MessageSquare,
  RefreshCw
} from 'lucide-react';
import PatientRow from './PatientRow';

const ClinicianList = ({ clinicians, patientsData, loading, onRefresh }) => {
  const [expandedClinicians, setExpandedClinicians] = useState(new Set());

  const toggleClinician = (clinicianId) => {
    const newExpanded = new Set(expandedClinicians);
    if (newExpanded.has(clinicianId)) {
      newExpanded.delete(clinicianId);
    } else {
      newExpanded.add(clinicianId);
    }
    setExpandedClinicians(newExpanded);
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return 'Never';

    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-gray-400" />
          <p className="text-gray-600">Loading clinicians and patients...</p>
        </CardContent>
      </Card>
    );
  }

  if (clinicians.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <Users className="h-16 w-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-xl font-semibold mb-2">No Clinicians Found</h3>
          <p className="text-gray-600">
            There are no clinicians registered in the system yet.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle>Clinicians & Their Patients</CardTitle>
          <Button
            onClick={onRefresh}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {clinicians.map((clinician) => {
            const patients = patientsData[clinician.id] || [];
            const activePatients = patients.filter(p => p.status === 'active').length;
            const isExpanded = expandedClinicians.has(clinician.id);

            return (
              <div key={clinician.id} className="border rounded-lg overflow-hidden">
                {/* Clinician Card Header */}
                <div
                  className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                  onClick={() => toggleClinician(clinician.id)}
                >
                  <div className="flex items-center gap-4 flex-1">
                    {/* Expand/Collapse Icon */}
                    <button className="text-gray-500 hover:text-gray-700">
                      {isExpanded ? (
                        <ChevronDown className="h-5 w-5" />
                      ) : (
                        <ChevronRight className="h-5 w-5" />
                      )}
                    </button>

                    {/* Clinician Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-semibold text-gray-900">
                          {clinician.name || clinician.email?.split('@')[0] || 'Unnamed Clinician'}
                        </h3>
                        {clinician.is_online && (
                          <span className="flex items-center gap-1 text-xs text-green-600">
                            <span className="h-2 w-2 bg-green-600 rounded-full"></span>
                            Online
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        {clinician.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-4 w-4" />
                            {clinician.email}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          {patients.length} patient{patients.length !== 1 ? 's' : ''}
                          {activePatients > 0 && ` (${activePatients} active)`}
                        </span>
                        {clinician.last_seen && (
                          <span className="flex items-center gap-1">
                            <Activity className="h-4 w-4" />
                            Last seen: {formatRelativeTime(clinician.last_seen)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Patient Count Badge */}
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                      {patients.length} {patients.length === 1 ? 'Patient' : 'Patients'}
                    </span>
                  </div>
                </div>

                {/* Expanded Patient List */}
                {isExpanded && (
                  <div className="bg-white border-t">
                    {patients.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Patient
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Room/Bed
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Exercises
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Last Active
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {patients.map((patient) => (
                              <PatientRow
                                key={patient.id}
                                patient={patient}
                                clinicianName={clinician.name || clinician.email?.split('@')[0]}
                              />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="p-8 text-center text-gray-500">
                        <Users className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                        <p>No patients assigned to this clinician yet.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default ClinicianList;
